import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import api, { mediaUrl } from "../api/axios";
import SearchCardModal from "./SearchCardModal";

const statusLabels = {
  showcase: "En coleccion",
  favorite: "Favorita",
  for_trade: "Disponible",
  for_sale: "Disponible",
  wanted: "Buscada"
};

const statusClass = status => `search-status-tag is-${status || "default"}`;

function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [searchText, setSearchText] = useState(query);
  const initialType = searchParams.get("type") || "cards";
  const [results, setResults] = useState({ cards: [], users: [], businesses: [], posts: [] });
  const [activeType, setActiveType] = useState(initialType);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedCard, setSelectedCard] = useState(null);

  useEffect(() => {
    setActiveType(initialType);
  }, [initialType]);

  useEffect(() => {
    setSearchText(query);
  }, [query]);

  useEffect(() => {
    const nextQuery = searchText.trim();
    if (nextQuery === query) return undefined;
    const timer = setTimeout(() => {
      const nextParams = new URLSearchParams(searchParams);
      if (nextQuery) nextParams.set("q", nextQuery);
      else nextParams.delete("q");
      nextParams.set("type", activeType);
      setSearchParams(nextParams, { replace: true });
    }, 280);
    return () => clearTimeout(timer);
  }, [activeType, query, searchParams, searchText, setSearchParams]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults({ cards: [], users: [], businesses: [], posts: [] });
      setLoading(false);
      setError("");
      return;
    }
    setLoading(true);
    setError("");
    api.get("/search", { params: { q: query.trim() } })
      .then(({ data }) => setResults(data || { cards: [], users: [], businesses: [], posts: [] }))
      .catch(() => setError("No se pudo buscar."))
      .finally(() => setLoading(false));
  }, [query]);

  const tabs = useMemo(() => [
    ["cards", `Cartas (${results.cards?.length || 0})`],
    ["users", `Coleccionistas (${results.users?.length || 0})`],
    ["businesses", `Tiendas (${results.businesses?.length || 0})`],
    ["posts", `Publicaciones (${results.posts?.length || 0})`]
  ], [results]);

  return (
    <Container className="page-band search-page">
      <div className="search-page-head">
        <div>
          <h1 className="section-title">Buscar en Cardora</h1>
          <p className="muted-copy mb-0">Encuentra cartas reales, coleccionistas, tiendas y publicaciones.</p>
        </div>
      </div>
      <Form className="search-page-form" onSubmit={event => event.preventDefault()} role="search">
        <i className="bi bi-search" aria-hidden="true" />
        <Form.Control
          autoFocus
          type="search"
          value={searchText}
          onChange={event => setSearchText(event.target.value)}
          placeholder="Nombre de carta, set, coleccionista o tienda..."
          aria-label="Buscar en Cardora"
        />
        {loading ? <Spinner animation="border" size="sm" /> : searchText && <button type="button" onClick={() => setSearchText("")} aria-label="Limpiar busqueda"><i className="bi bi-x-lg" /></button>}
      </Form>
      {error && <div className="inline-page-notice mb-3">{error}</div>}
      {query.trim().length < 2 ? (
        <div className="search-page-empty">
          <i className="bi bi-search-heart" />
          <strong>Busca dentro de la comunidad</strong>
          <span>Escribe al menos dos caracteres para empezar.</span>
        </div>
      ) : <>
      <div className="feed-filter-bar search-type-tabs mb-4">
        {tabs.map(([value, label]) => (
          <Button key={value} size="sm" variant={activeType === value ? "dark" : "outline-dark"} onClick={() => setActiveType(value)}>
            {label}
          </Button>
        ))}
      </div>

      {activeType === "cards" && (
        <Row className="g-3">
          {!loading && results.cards?.length === 0 && <Col><Card className="cardora-card"><Card.Body>No encontramos cartas para <strong>{query}</strong>.</Card.Body></Card></Col>}
          {results.cards?.map(card => (
            <Col md={6} xl={4} key={card.user_card_id}>
              <button type="button" className="search-card-result" onClick={() => setSelectedCard(card)}>
                {card.image_url ? <img src={mediaUrl(card.image_url)} alt="" /> : <div className="search-card-placeholder" />}
                <div>
                  <div className="search-card-title-row">
                    <strong>{card.card_name}</strong>
                    <span className={statusClass(card.status)}>{statusLabels[card.status] || card.status}</span>
                  </div>
                  <span>{card.has_real_photo ? "Foto real · " : ""}{card.display_name}</span>
                  <small>{[card.set_name, card.collector_number, card.card_type, card.energy_type, card.condition].filter(Boolean).join(" · ")}</small>
                </div>
              </button>
            </Col>
          ))}
        </Row>
      )}

      {activeType === "users" && (
        <Row className="g-3">
          {results.users?.map(user => <Col md={6} xl={4} key={user.id}><Link className="search-text-result" to={`/u/${user.handle || user.id}`}><strong>{user.display_name}</strong><span>{user.handle ? `@${user.handle}` : user.bio || "Coleccionista Cardora"}</span></Link></Col>)}
        </Row>
      )}

      {activeType === "businesses" && (
        <Row className="g-3">
          {results.businesses?.map(business => <Col md={6} xl={4} key={business.id}><Link className="search-text-result" to={`/store/${business.slug}`}><strong>{business.name}</strong><span>{business.description || business.slug}</span></Link></Col>)}
        </Row>
      )}

      {activeType === "posts" && (
        <div className="d-grid gap-3">
          {results.posts?.map(post => <Card className="cardora-card" key={post.id}><Card.Body><Badge bg="light" text="dark">{post.type}</Badge><p className="mb-1 mt-2">{post.body}</p><Link to={`/u/${post.user_id}`}>{post.display_name}</Link></Card.Body></Card>)}
        </div>
      )}
      </>}
      <SearchCardModal card={selectedCard} onClose={() => setSelectedCard(null)} onChange={updated => { setSelectedCard(updated); setResults(current => ({ ...current, cards: current.cards.map(card => card.user_card_id === updated.user_card_id ? updated : card) })); }} />
    </Container>
  );
}

export default SearchResults;